import { Link } from 'react-router-dom';
import { ChevronRight, Clock, MessageCircle } from 'lucide-react';
import { SUCCESS_SCORE_MAX, type SuccessScoreComponent } from '@mlm/shared/coaching';
import { cn } from '@mlm/ui/lib/cn';
import { coaching, type Action, type ProgramSummary, type Today } from '../../lib/api';
import { actionMeta, minutesLabel } from '../../lib/format';
import { communityApp, streakWeek } from '../../lib/home';
import { useProgram, useToggleAction } from '../../lib/queries';
import { scoreTip, scoreTipPart } from '../../lib/score';
import { useAffirmation } from '../../lib/use-affirmation';
import { DoneMark, PendingMark, ScoreRing, StarGlyph } from '../visual';

/*
 * Home's "then act" half (2026-10 redesign): the #1 action, the Success
 * Score, the rituals, the program and the affirmation, as rounded cards. The
 * behaviour is the old Home's: the same mutations, routes and practices.
 */

const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-verde';
const pill =
  'inline-flex items-center justify-center rounded-full font-mono uppercase tracking-[0.14em] transition-colors disabled:cursor-not-allowed disabled:opacity-50';
const goldPill = cn(pill, 'bg-gold text-ink hover:bg-[#c29d4c]', focusRing);
const outlinePill = cn(
  pill,
  'border border-[rgba(16,32,26,0.22)] text-ink hover:border-[rgba(16,32,26,0.5)]',
  focusRing,
);
const eyebrow = 'eyebrow text-[0.62rem]';

// ── The #1 action ───────────────────────────────────────────────────────────

function ActionRow({ action, meta }: { action: Action; meta: string | null }) {
  const toggle = useToggleAction();
  const done = action.status === 'done';
  return (
    <button
      type="button"
      onClick={() => toggle.mutate(action)}
      className={cn(
        'flex w-full items-center gap-3 border-t border-[#EFEDE4] py-3 text-left',
        focusRing,
      )}
    >
      {done ? <DoneMark /> : <PendingMark />}
      <span className={cn('min-w-0 flex-1 text-sm', done && 'text-sage line-through')}>
        {action.title}
      </span>
      <span className="shrink-0 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-sage-deep">
        {done ? 'Done' : meta}
      </span>
      <span className="sr-only">{done ? 'Mark as not done' : 'Mark as done'}</span>
    </button>
  );
}

/** Today's #1 action and the rest of the day's list, each a one-tap toggle. */
export function ActionCard({ today, layout }: { today: Today; layout: 'phone' | 'desktop' }) {
  const toggle = useToggleAction();
  const phone = layout === 'phone';
  const [primary, ...supporting] = today.actions;
  const milestone = today.nextMilestone;
  const hasRows = supporting.length > 0 || (!phone && milestone);

  return (
    <div
      className={cn(
        'flex flex-col border border-border bg-surface',
        phone ? 'mx-5 mt-3 rounded-[20px] px-[18px] pt-[18px]' : 'rounded-3xl px-[22px] pt-5',
        hasRows ? (phone ? 'pb-1' : 'pb-1.5') : phone ? 'pb-[18px]' : 'pb-5',
      )}
    >
      <div className="flex items-center gap-2">
        <StarGlyph />
        <p className={eyebrow}>Your #1 action today</p>
      </div>
      {primary ? (
        <>
          <h2
            className={cn(
              'text-pretty font-display font-medium leading-[1.2] text-ink',
              phone ? 'mt-2.5 text-[22px]' : 'mt-3 text-[28px] leading-[1.15]',
            )}
          >
            {primary.title}
          </h2>
          {(primary.estimatedMinutes || today.primaryGoal) && (
            <p className={cn('text-ink/60', phone ? 'mt-1.5 text-[13px]' : 'mt-2 text-sm')}>
              {[minutesLabel(primary.estimatedMinutes), today.primaryGoal?.title]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
          <div className={cn('flex items-center gap-3.5', phone ? 'mt-3.5' : 'mt-4')}>
            {primary.status === 'done' ? (
              <p className="flex min-h-12 items-center gap-2 text-sm text-sage">
                <DoneMark size={20} /> Done. One step closer.
              </p>
            ) : (
              <button
                type="button"
                disabled={toggle.isPending}
                onClick={() => toggle.mutate(primary)}
                className={cn(
                  goldPill,
                  phone
                    ? 'min-h-12 w-full text-[0.68rem] tracking-[0.16em]'
                    : 'min-h-[46px] px-[26px] text-[0.66rem] tracking-[0.16em]',
                )}
              >
                Mark as done
              </button>
            )}
            {!phone && (
              <Link
                to="/achieve/actions"
                className="font-mono text-[0.62rem] uppercase tracking-[0.14em] text-sage underline underline-offset-4 hover:text-ink"
              >
                Plan actions
              </Link>
            )}
          </div>
        </>
      ) : (
        <>
          <h2
            className={cn(
              'font-display font-medium text-ink',
              phone ? 'mt-2.5 text-[22px]' : 'mt-3 text-[28px]',
            )}
          >
            You're clear for today.
          </h2>
          <p className="mt-1.5 text-sm text-ink/60">
            Enjoy the progress you've already made, or add one meaningful action.
          </p>
          <Link
            to="/achieve/actions"
            className={cn(outlinePill, 'mt-4 min-h-11 self-start px-5 text-[0.62rem]')}
          >
            Add an action
          </Link>
        </>
      )}
      {hasRows && (
        <div className={cn(phone ? 'mt-2' : 'mt-auto pt-3.5')}>
          {supporting.map((a) => (
            <ActionRow
              key={a.id}
              action={a}
              meta={phone ? minutesLabel(a.estimatedMinutes) : actionMeta(a)}
            />
          ))}
          {!phone && milestone && (
            <Link
              to={`/achieve/goals/${milestone.goalId}`}
              className={cn('flex items-center gap-3 border-t border-[#EFEDE4] py-3', focusRing)}
            >
              <PendingMark milestone />
              <span className="min-w-0 flex-1 text-sm text-ink">{milestone.title}</span>
              <span className="shrink-0 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-sage-deep">
                Next milestone
              </span>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

// ── Success Score ───────────────────────────────────────────────────────────

function StreakDots({ today }: { today: Today }) {
  const week = streakWeek(today.day, today.streak);
  return (
    <div className="flex items-center gap-1.5" aria-hidden>
      {week.map((d) => (
        <span key={d.day} className="flex flex-col items-center gap-1">
          <span
            className={cn('h-3 w-3 rounded-full', d.filled ? 'bg-verde' : 'bg-ink/10')}
            style={d.today ? { boxShadow: '0 0 0 2px #F7F5EF, 0 0 0 3.5px #B08D3F' } : undefined}
          />
          <span className="font-mono text-[0.5rem] text-sage-deep">{d.letter}</span>
        </span>
      ))}
    </div>
  );
}

const SCORE_SHORT: Record<SuccessScoreComponent, string> = {
  actions: 'Actions',
  morningRitual: 'Morning',
  mindset: 'Mindset',
  alignment: 'Alignment',
  eveningReview: 'Evening',
  goalProgress: 'Goals',
};

/** Today's Success Score as a segmented ring with the week's streak; opens the breakdown. */
export function ScoreCard({ today, layout }: { today: Today; layout: 'phone' | 'desktop' }) {
  const phone = layout === 'phone';
  const tip = scoreTip(today.score);
  const streak = today.streak.current;
  const streakLine = (
    <p className="mt-2 font-mono text-[0.6rem] uppercase tracking-[0.14em] text-verde">
      {streak > 0 ? `${streak}-day streak` : 'Your streak starts today'}
    </p>
  );
  return (
    <Link
      to="/achieve/score"
      aria-label={`Today's Success Score: ${today.score.total}, ${today.score.label}. Open the breakdown.`}
      className={cn(
        'bg-score-wash relative block overflow-hidden rounded-3xl shadow-[0_14px_34px_-22px_rgba(16,32,26,0.35)]',
        phone ? 'mx-5 mt-3 px-[18px] pb-4 pt-[18px]' : 'p-5',
        focusRing,
      )}
    >
      <div
        aria-hidden
        className="glow-gold-soft pointer-events-none absolute -right-[50px] -top-[60px] h-[200px] w-[200px] rounded-full"
      />
      {phone && (
        <div
          aria-hidden
          className="glow-verde-soft pointer-events-none absolute -bottom-[70px] -left-10 h-[180px] w-[180px] rounded-full"
        />
      )}
      {!phone && <p className={cn(eyebrow, 'relative')}>Today's Success Score</p>}
      <div className={cn('relative flex items-center gap-4', !phone && 'mt-3')}>
        <ScoreRing
          score={today.score}
          size={phone ? 132 : 140}
          highlight={scoreTipPart(today.score)}
          numberClass={phone ? 'text-[40px]' : 'text-[42px]'}
        />
        <div className="min-w-0 flex-1">
          {phone ? (
            <>
              <p className={eyebrow}>Success Score</p>
              {tip && <p className="mt-1.5 text-[13px] leading-[1.45] text-ink/75">{tip}</p>}
              <div className="mt-3">
                <StreakDots today={today} />
              </div>
              {streakLine}
            </>
          ) : (
            <>
              <StreakDots today={today} />
              {streakLine}
              {tip && <p className="mt-2.5 text-[13px] leading-[1.45] text-ink/75">{tip}</p>}
            </>
          )}
        </div>
      </div>
      {!phone && (
        <div className="relative mt-3.5 grid grid-cols-3 gap-x-2.5 gap-y-1.5">
          {(Object.keys(SUCCESS_SCORE_MAX) as SuccessScoreComponent[]).map((part) => (
            <span
              key={part}
              className="flex items-baseline justify-between gap-1.5 font-mono text-[0.56rem] uppercase tracking-[0.08em] text-sage"
            >
              <span>{SCORE_SHORT[part]}</span>
              <span className="text-ink">
                {today.score.breakdown[part]}/{SUCCESS_SCORE_MAX[part]}
              </span>
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}

// ── Rituals ─────────────────────────────────────────────────────────────────

function ritualMeta(today: Today, minutes: number | null) {
  const { morning, evening } = today.ritual;
  const morningMeta = morning.completed
    ? ['Done today', minutes ? `${minutes} min` : null].filter(Boolean).join(' · ')
    : morning.started && morning.stepsTotal > 0
      ? `${morning.stepsCompleted} of ${morning.stepsTotal} steps`
      : minutes
        ? `${minutes} min`
        : 'Start your day with intention';
  const eveningMeta = evening.completed
    ? evening.tomorrowPriority
      ? `Tomorrow: ${evening.tomorrowPriority}`
      : 'Done today'
    : "Reflect, then set tomorrow's priority";
  return { morningMeta, eveningMeta };
}

/** Phone: the morning ritual and the evening review as two tiles. */
export function RitualTiles({ today, minutes }: { today: Today; minutes: number | null }) {
  const { morning, evening } = today.ritual;
  const { morningMeta, eveningMeta } = ritualMeta(today, minutes);
  const tile = cn(
    'flex flex-col gap-2.5 rounded-[18px] border border-border bg-surface p-3.5 text-ink transition-colors hover:border-[rgba(16,32,26,0.3)]',
    focusRing,
  );
  return (
    <section className="mx-5 mt-[26px]">
      <p className={cn(eyebrow, 'mb-2.5')}>Daily rituals</p>
      <div className="grid grid-cols-2 gap-2.5">
        <Link to="/ritual/morning" className={tile}>
          {morning.completed ? <DoneMark size={26} /> : <PendingMark size={26} />}
          <span>
            <span className="block text-[15px] font-medium">Morning ritual</span>
            <span className="mt-0.5 block text-xs text-sage-deep">{morningMeta}</span>
          </span>
        </Link>
        <Link to="/review/evening" className={tile}>
          {evening.completed ? (
            <DoneMark size={26} />
          ) : (
            <Clock className="h-[26px] w-[26px] text-sage" strokeWidth={1.2} aria-hidden />
          )}
          <span>
            <span className="block text-[15px] font-medium">Evening review</span>
            <span className="mt-0.5 block text-xs text-sage-deep">{eveningMeta}</span>
          </span>
        </Link>
      </div>
    </section>
  );
}

/** Desktop: the same two rituals as chips beside the greeting. */
export function RitualChips({ today }: { today: Today }) {
  const { morning, evening } = today.ritual;
  const chip = cn(
    'inline-flex min-h-11 items-center rounded-full px-[18px] font-mono text-[0.64rem] uppercase tracking-[0.14em] transition-colors',
    focusRing,
  );
  const done = cn(
    chip,
    'border border-[rgba(16,32,26,0.2)] bg-surface text-ink hover:border-[rgba(16,32,26,0.4)]',
  );
  const open = cn(chip, 'bg-wash text-verde hover:bg-[#D3E7C0]');
  return (
    <div className="flex shrink-0 gap-2.5">
      <Link to="/ritual/morning" className={morning.completed ? done : open}>
        {morning.completed ? 'Morning ritual · Done' : 'Morning ritual'}
      </Link>
      <Link to="/review/evening" className={evening.completed ? done : open}>
        {evening.completed ? 'Evening review · Done' : 'Evening review'}
      </Link>
    </div>
  );
}

// ── Program ─────────────────────────────────────────────────────────────────

const DAY_STYLE = {
  done: 'border-verde bg-verde text-paper',
  today: 'border-gold bg-gold text-ink',
  later: 'border-ink/[0.16] bg-ink/[0.05] text-sage',
} as const;

/** The guided program the member is in, as its journey: done, today, still to come. */
export function ProgramJourneyCard({
  program,
  layout,
}: {
  program: ProgramSummary;
  layout: 'phone' | 'desktop';
}) {
  const detail = useProgram(program.id);
  const e = program.enrollment!;
  const phone = layout === 'phone';
  const startsLater =
    e.completedDays === 0 &&
    e.currentDay === 1 &&
    e.startedOn > new Date().toISOString().slice(0, 10);
  const days = Array.from({ length: program.daysCount }, (_, i) => {
    const n = i + 1;
    const row = detail.data?.days.find((d) => d.dayNo === n);
    const state: keyof typeof DAY_STYLE =
      row?.state === 'done'
        ? 'done'
        : (row ? row.state === 'today' : n === e.currentDay)
          ? 'today'
          : 'later';
    return { n, state };
  });
  const todayTitle = detail.data?.days.find((d) => d.dayNo === e.currentDay)?.title ?? null;
  const open = (
    <Link
      to={`/programs/${program.id}/days/${e.currentDay}`}
      className={cn(
        goldPill,
        'min-h-10 shrink-0 px-4 text-[0.62rem]',
        !phone && 'min-h-[42px] px-[18px]',
      )}
    >
      {startsLater ? 'Read day 1' : `Open day ${e.currentDay}`}
    </Link>
  );
  const grid = (
    <div
      className={cn(
        'grid grid-cols-7 gap-2',
        phone ? 'mt-3.5 max-w-[230px]' : 'w-[250px] shrink-0',
      )}
    >
      {days.map((d) => (
        <span
          key={d.n}
          className={cn(
            'flex items-center justify-center rounded-full border font-mono',
            phone ? 'h-6 text-[0.56rem]' : 'h-7 text-[0.6rem]',
            DAY_STYLE[d.state],
          )}
        >
          {d.n}
        </span>
      ))}
    </div>
  );

  return (
    <section
      className={cn(
        'relative overflow-hidden border border-border bg-surface',
        phone
          ? 'mx-5 mt-[22px] rounded-[20px] p-[18px]'
          : 'flex items-center gap-7 rounded-3xl px-6 py-[22px]',
      )}
    >
      <div
        aria-hidden
        className={cn(
          'glow-gold-soft pointer-events-none absolute rounded-full',
          phone
            ? '-right-[60px] -top-10 h-[220px] w-[220px]'
            : '-right-20 -top-[60px] h-[280px] w-[280px]',
        )}
      />
      <div className="relative min-w-0 flex-1">
        <p className={eyebrow}>
          Your program · Day {e.currentDay} of {program.daysCount}
        </p>
        <h2
          className={cn(
            'mt-2 font-display font-medium leading-[1.2] text-ink',
            phone ? 'text-[22px]' : 'text-[26px] leading-[1.15]',
          )}
        >
          {program.title}
        </h2>
        {phone ? (
          <>
            {grid}
            <div className="mt-4 flex items-center justify-between gap-3">
              <span className="min-w-0 text-[13px] text-ink/65">
                {todayTitle
                  ? `Today: ${todayTitle}`
                  : `${e.completedDays} of ${program.daysCount} days done`}
              </span>
              {open}
            </div>
          </>
        ) : (
          <>
            <p className="mt-2 text-sm text-ink/65">
              {todayTitle ? `Today: ${todayTitle}. ` : ''}
              {program.tagline}
            </p>
            <div className="mt-4 flex gap-2.5">
              {open}
              <Link
                to="/programs"
                className={cn(outlinePill, 'min-h-[42px] px-[18px] text-[0.62rem]')}
              >
                All programs
              </Link>
            </div>
          </>
        )}
      </div>
      {!phone && <div className="relative">{grid}</div>}
    </section>
  );
}

// ── Affirmation ─────────────────────────────────────────────────────────────

/** Today's affirmation: "I said it" moves the Mindset part of the score, Share forwards it. */
export function AffirmationCard({ today, layout }: { today: Today; layout: 'phone' | 'desktop' }) {
  const { affirmed, pending, affirm, share } = useAffirmation(today);
  const phone = layout === 'phone';
  return (
    <section
      className={cn(
        'bg-affirm-wash relative flex flex-col overflow-hidden border border-gold/20',
        phone ? 'mx-5 mt-[22px] rounded-[20px] p-[18px]' : 'rounded-3xl p-5',
      )}
    >
      <p className={cn(eyebrow, 'text-[#8A7342]')}>Today's affirmation</p>
      <p
        className={cn(
          'mt-2.5 text-pretty font-display font-normal italic leading-[1.25] text-ink',
          phone ? 'text-[22px]' : 'text-[21px]',
        )}
      >
        “{today.affirmation}”
      </p>
      <div className={cn('flex items-center gap-2.5', phone ? 'mt-3.5' : 'mt-auto pt-3.5')}>
        {affirmed ? (
          <p className="flex min-h-[42px] items-center gap-2 text-sm text-sage">
            <DoneMark size={20} /> Repeated today
          </p>
        ) : (
          <button
            type="button"
            disabled={pending}
            onClick={affirm}
            className={cn(
              pill,
              'min-h-[42px] border border-ink/25 bg-white/60 px-[18px] text-[0.64rem] text-ink hover:border-ink/50',
              focusRing,
            )}
          >
            I said it
          </button>
        )}
        <button
          type="button"
          onClick={() => void share()}
          className={cn(
            'min-h-[42px] px-3.5 font-mono text-[0.64rem] uppercase tracking-[0.14em] text-sage underline underline-offset-4 hover:text-ink',
            focusRing,
          )}
        >
          Share
        </button>
      </div>
    </section>
  );
}

// ── Milestone and community ────────────────────────────────────────────────

type Community = { url: string; label: string | null } | null;

function CommunityLink({
  community,
  className,
}: {
  community: NonNullable<Community>;
  className: string;
}) {
  const app = communityApp(community.url);
  return (
    <a
      href={community.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => coaching.event('community_opened')}
      className={className}
    >
      <MessageCircle
        className="h-[22px] w-[22px] shrink-0 text-sage"
        strokeWidth={1.5}
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-ink">
          {community.label ?? 'Join the community'}
        </span>
        <span className="mt-0.5 block font-mono text-[0.56rem] uppercase tracking-[0.14em] text-sage-deep">
          Community · {app ? `Opens in ${app}` : 'Opens outside the app'}
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-sage-deep" aria-hidden />
    </a>
  );
}

/** Phone: the next milestone and the community, one card of rows. */
export function PhoneLinks({ today, community }: { today: Today; community: Community }) {
  const m = today.nextMilestone;
  if (!m && !community) return null;
  const row = cn(
    'flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-canvas',
    focusRing,
  );
  return (
    <div className="mx-5 mt-3 divide-y divide-[#EFEDE4] overflow-hidden rounded-[20px] border border-border bg-surface">
      {m && (
        <Link to={`/achieve/goals/${m.goalId}`} className={row}>
          <PendingMark />
          <span className="min-w-0 flex-1">
            <span className="block text-sm text-ink">{m.title}</span>
            <span className="mt-0.5 block font-mono text-[0.56rem] uppercase tracking-[0.14em] text-sage-deep">
              Next milestone{today.primaryGoal ? ` · ${today.primaryGoal.title}` : ''}
            </span>
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-sage-deep" aria-hidden />
        </Link>
      )}
      {community && <CommunityLink community={community} className={row} />}
    </div>
  );
}

/** Desktop: the community on its own row card. */
export function CommunityRow({ community }: { community: Community }) {
  if (!community) return null;
  return (
    <CommunityLink
      community={community}
      className={cn(
        'mt-[26px] flex max-w-[380px] items-center gap-3.5 rounded-[20px] border border-border bg-surface px-5 py-4 transition-colors hover:border-[rgba(16,32,26,0.3)]',
        focusRing,
      )}
    />
  );
}
